export const blogsData = [
  {
    id: "aws-outage-oct-2025",
    title: "Inside the October 2025 AWS Outage: What Went Down?",
    subHeading: "From Lambda Failures to Global App Impact",
    labels: ["AWS", "Cloud", "Incident"],
    date: "2025-10-19",
    summary:
      "AWS experienced a major outage affecting Lambda, DynamoDB, and S3 in ap-south-1. This post explains what caused it and how startups coped.",
    fullContent: `
      <h2>What Happened?</h2>
      Last week, AWS's ap-south-1 region faced an unprecedented outage, with Lambda, DynamoDB, and S3 services failing for several hours.<br/>
      <h3>Root Cause Analysis</h3>
      The initial impact was triggered by a networking issue in AWS datacenter, cascading failures to storage and compute services.<br/>
      <ul>
        <li><b>Impact on Lambda:</b> Cold start times increased, functions timed out, and workflows failed across India startups.</li>
        <li><b>DynamoDB & S3:</b> Database writes and asset retrieval stalled, causing severe downtime on customer-facing apps.</li>
      </ul>
      <h3>User Stories</h3>
      Dev teams scrambled to re-route traffic and provision backup infrastructure on other clouds. SaaS platforms experienced degraded experience.<br/>
      <h3>Lessons for Architects</h3>
      - Always architect for multi-region failover<br/>
      - Keep runbooks for cloud outages<br/>
      <h3>Takeaway</h3>
      Disaster recovery is not a luxury it's a necessity for every cloud engineer.
    `,
  },
  {
    id: "kafka-latency-crisis",
    title: "Kafka Latency Crisis on k8s: A Fintech’s Ordeal",
    subHeading: "Microservices Ground to a Halt",
    labels: ["Kubernetes", "Kafka", "Incident"],
    date: "2025-10-14",
    summary:
      "A fintech startup deployed Kafka on Kubernetes, only to face severe message delays due to node resource exhaustion.",
    fullContent: `
      <h2>Background</h2>
      The engineering team adopted Kafka for real-time settlements but high traffic exposed serious latency in the k8s nodes.
      <h3>Symptoms</h3>
      - Consumer lag spiked to minutes<br/>
      - Alerts fired from payment workflows<br/>
      <h3>Investigation</h3>
      Node resources were exhausted by improper JVM tuning and pod eviction. Memory and CPU spikes visible in monitoring dashboards.<br/>
      <h3>Recovery Steps</h3>
      Restarted Kafka brokers on reserved nodes, tuned resource requests, enabled better liveness probes.<br/>
      <h3>Key Lessons</h3>
      Ensure strict node resource profiles, and always alert on JVM memory.
    `,
  },
  {
    id: "cicd-broke-prod",
    title: "When CI/CD Broke Production in 2 Minutes",
    subHeading: "A Fumbled Pipeline & Major Rollback",
    labels: ["CI/CD", "Incident", "Rollback"],
    date: "2025-10-12",
    summary:
      "A release pipeline misconfiguration caused an artifact mismatch, failing 17 production pods instantly. Rollback and monitoring to the rescue.",
    fullContent: `
      <h2>The Breakdown</h2>
      A Java artifact build step misused the wrong environment flag. When deployed, 17 pods crashed instantly.<br/>
      <h3>How Observability Helps</h3>
      Real-time Prometheus alerts detected high error rates. Rollback was started within three minutes.<br/>
      <h3>Timeline</h3>
      - 10:00: Deploy started<br/>
      - 10:02: Errors detected<br/>
      - 10:03: Rollback initiated<br/>
      - 10:07: Recovery confirmed
      <h3>Takeaway</h3>
      Always have alerting and rollback automation in every pipeline.
    `,
  },
  {
    id: "istio-sidecar-crash",
    title: "Istio Sidecar Injection Caused App Crash: Postmortem",
    subHeading: "Envoy Misconfig and Service Impact",
    labels: ["Istio", "Incident", "Kubernetes"],
    date: "2025-10-08",
    summary:
      "An e-commerce company faced outages when Istio sidecar injection caused service health checks to fail. Debugging lessons for microservices.",
    fullContent: `
      <h2>Problem</h2>
      Istio auto-injected Envoy proxies misconfigured port assignments, causing health check failures on production services.<br/>
      <h3>What Went Wrong?</h3>
      Sidecar resources were not tuned. The rolling update process missed readiness gates.<br/>
      <h3>Best Practices</h3>
      - Test sidecar configs in staging environments.<br/>
      - Document all custom health probes.<br/>
    `,
  },
  {
    id: "docker-pull-rate-limits",
    title: "Docker Pull Rate Limits Hit Dev Teams Hard",
    subHeading: "CI Builds Stalled Worldwide",
    labels: ["Docker", "CI/CD", "Incident"],
    date: "2025-10-04",
    summary:
      "Sudden DockerHub rate limits led to broken CI jobs for open-source contributors. Mitigation steps and alternate registries discussed.",
    fullContent: `
      <h2>Incident</h2>
      Many projects started failing to build when DockerHub imposed new rate limits.<br/>
      <h3>Symptoms</h3>
      - CI jobs failed with image pull errors<br/>
      <h3>Solutions</h3>
      - Use authenticated DockerHub pulls<br/>
      - Mirror images to AWS ECR or GitHub Container Registry<br/>
      <h3>Automation</h3>
      Update CI pipeline to use redundant image sources for key builds.
    `,
  },
  {
    id: "k8s-pod-eviction-memory-leak",
    date: "2025-09-28",
    title: "Surviving Cluster-Wide Pod Eviction in Kubernetes",
    subHeading: "A Memory Leak in Practice",
    labels: ["Kubernetes", "Incident", "Python"],
    summary:
      "A SaaS company’s cluster was hit with mass pod evictions caused by a memory leak in a popular Python library. Read about the detection, troubleshooting, and long-term prevention.",
    fullContent: `
    <h2>Overview</h2>
    A hidden memory leak in a commonly used Python library triggered OOM errors, evicting pods across production clusters.
    <h3>Detection</h3>
    - Monitoring dashboards showed memory usage trending up over hours.<br/>
    - OOMKilled events spiked unexpectedly.<br/>
    <h3>Troubleshooting</h3>
    The SRE team used <code>kubectl top pods</code> and container metrics to isolate the culprit library.<br/>
    <h3>Prevention</h3>
    - Upgraded Python package<br/>
    - Set resource limits/requests for all workloads<br/>
    - Enabled autoscaling for memory-bound pods<br/>
    <h3>Takeaway</h3>
    Continuous resource monitoring is vital to catch leaks before mass pod evictions bring down services. Automated alerts are crucial for fast mitigation.
  `,
  },
  {
    id: "vpc-peering-broke-connectivity",
    date: "2025-09-25",
    title: "Why Our VPC Peering Broke App Connectivity",
    subHeading: "Misconfigured Routes, Real Loss",
    labels: ["AWS", "Networking", "Incident"],
    summary:
      "During a planned AWS expansion, improper VPC route tables left services unreachable. Trace the analysis, rollback, and postmortem documentation.",
    fullContent: `
    <h2>Incident Overview</h2>
    Our team expanded AWS infrastructure across regions. New VPC peering arrangements were made.<br/>
    <h3>Problem</h3>
    - Route tables didn’t include all required CIDR blocks.<br/>
    - Servers were unreachable for key APIs.<br/>
    <h3>Resolution</h3>
    - Rolled back faulty routes<br/>
    - Audited and documented all VPC connections<br/>
    <h3>Documentation</h3>
    Every VPC peering change now gets a full test plan and rollback strategy.<br/>
    <h3>Lessons Learned</h3>
    Always validate network access before committing changes in production.
  `,
  },
  {
    id: "helm-namespace-multi-tenant",
    date: "2025-09-17",
    title: "Multi-Tenant Helm Deploy Gone Wrong",
    subHeading: "Namespace Confusion & Data Exposure",
    labels: ["Kubernetes", "Helm", "Incident"],
    summary:
      "A misconfigured Helm deployment accidentally exposed secrets between tenants. Learn how RBAC and namespace hygiene became top priorities.",
    fullContent: `
    <h2>Background</h2>
    The team deployed updates using a new Helm chart, targeting multiple tenant namespaces.<br/>
    <h3>What Went Wrong?</h3>
    - Helm template failed to separate secrets per namespace<br/>
    - Data exposure risk across tenants<br/>
    <h3>How It Was Fixed</h3>
    - Tightened RBAC roles<br/>
    - Updated Helm charts for explicit namespace handling<br/>
    <h3>Preventative Steps</h3>
    Namespace hygiene and Helm template reviews are now part of every release.
  `,
  },
  {
    id: "datadog-billing-explosion",
    date: "2025-09-12",
    title: "Datadog Billing Explosion After Logging Update",
    subHeading: "Excess Data and Monitoring Costs",
    labels: ["Kubernetes", "Datadog", "Incident"],
    summary:
      "Continuous logging added 3x billing overnight for Datadog on k8s clusters. Explore root cause and logging strategy fixes.",
    fullContent: `
    <h2>Incident Recap</h2>
    Overnight, our Datadog billing tripled after deploying a new logging agent.<br/>
    <h3>Root Cause</h3>
    - New logging config captured verbose debug logs from all containers<br/>
    - Retention policies unchanged<br/>
    <h3>Mitigation</h3>
    - Applied log sampling and filtering rules<br/>
    - Lowered retention for non-critical logs<br/>
    <h3>Key Lesson</h3>
    Always test logging agent changes in a dev environment before full rollout.
  `,
  },
  {
    id: "istio-mtls-legacy-break",
    date: "2025-09-04",
    title: "Istio mTLS Rollout and Legacy Service Breakage",
    subHeading: "Secure by Default - Unexpected Downtime",
    labels: ["Istio", "Security", "Incident"],
    summary:
      "Rolling out Istio mTLS led to legacy services failing to connect, causing hours of outage. Read how backwards compatibility is key.",
    fullContent: `
    <h2>Change Summary</h2>
    Istio mTLS was enabled to improve internal traffic security.<br/>
    <h3>Impact</h3>
    - Legacy Java apps did not support mTLS<br/>
    - Connection failures across production stack<br/>
    <h3>Fast Recovery</h3>
    - Disabled mTLS on legacy namespaces<br/>
    - Plan for phased rollout<br/>
    <h3>Conclusion</h3>
    Compatibility and rollout planning are essential for zero-downtime security upgrades.
  `,
  },
  {
    id: "eks-upgrade-deprecated-crds",
    date: "2025-08-28",
    title: "Unplanned EKS Upgrade and Deprecated Features",
    subHeading: "API Deprecation Pain",
    labels: ["AWS", "Kubernetes", "Incident"],
    summary:
      "A rushed EKS upgrade left custom CRDs out of action. Learn how to safely plan for version bumps in managed Kubernetes.",
    fullContent: `
    <h2>Incident</h2>
    EKS upgrade occurred without a full inventory of CRDs.<br/>
    <h3>Result</h3>
    - Legacy APIs were deprecated<br/>
    - Custom tools and dashboards failed<br/>
    <h3>Mitigation</h3>
    - Inventoried all CRDs pre-upgrade<br/>
    - Used <code>kubectl api-resources</code> for review<br/>
    <h3>Lessons Learned</h3>
    Never upgrade managed clusters without complete API compatibility testing.
  `,
  },
  {
    id: "cicd-pipeline-secrets-leak",
    date: "2025-08-14",
    title: "CI/CD Pipeline Secrets Leak: Lessons Learned",
    subHeading: "A Misconfigured GitHub Action",
    labels: ["CI/CD", "Security", "Incident"],
    summary:
      "A CI job accidentally pushed secrets into logs, causing public exposure. See how mitigation and communication best practices helped limit damage.",
    fullContent: `
    <h2>Incident</h2>
    A new GitHub Action step was introduced into our CI/CD workflow. Due to a misconfiguration, secrets from environment variables were echoed into the logs by a debug command.<br/>
    <h3>How It Was Discovered</h3>
    A security bot detected secrets exposed in public logs within 10 minutes.<br/>
    <h3>Mitigation Steps</h3>
    - Revoked leaked credentials and generated new secrets<br/>
    - Scrubbed logs and forced log expiry<br/>
    <h3>Best Practices Going Forward</h3>
    - Apply <code>set +x</code> before sensitive steps in shell<br/>
    - Use secret masking built into CI providers<br/>
    <h3>Communication</h3>
    Informed affected stakeholders and published a postmortem.<br/>
    <h3>Takeaway</h3>
    Always audit log outputs after CI changes, especially when handling secrets.
  `,
  },
  {
    id: "docker-compose-app-fail-cloud",
    date: "2025-08-10",
    title: "Why Our Docker Compose App Failed On the Cloud",
    subHeading: "Environment Variables & Compatibility",
    labels: ["Docker", "AWS", "Incident"],
    summary:
      "A local app that thrived under Docker Compose failed to run on AWS ECS. The difference? Environment variable and networking mismatches.",
    fullContent: `
    <h2>The Problem</h2>
    The team had an app that worked perfectly under Docker Compose but failed once run as containers on AWS ECS.<br/>
    <h3>Root Causes</h3>
    - Environment variables not injected in the same way<br/>
    - Docker Compose networking model different from cloud VPC networking<br/>
    <h3>How We Fixed It</h3>
    - Updated task definition to match cloud expectations<br/>
    - Used SSM Parameter Store for secure environment management<br/>
    <h3>Takeaway</h3>
    Test against production-like environments before migration to cloud containers.
  `,
  },
  {
    id: "k8s-node-autoscaling-overshoot",
    date: "2025-07-30",
    title: "Kubernetes Node Auto-Scaling Gone Haywire",
    subHeading: "Billing and Capacity Overshoot",
    labels: ["Kubernetes", "AWS", "Incident"],
    summary:
      "Cluster auto-scaling tripled costs overnight due to a stuck pod restarting loop. Explore auto-scaling trigger, resource policies, and prevention.",
    fullContent: `
    <h2>Event Summary</h2>
    A failing pod with restartCrashLoopBackoff kept requesting new node capacity, causing the node group to balloon to 3x normal.<br/>
    <h3>Symptoms</h3>
    - Overnight AWS bill spike<br/>
    - Large number of unhealthy nodes created<br/>
    <h3>Diagnosis</h3>
    - Pod was stuck due to a missing secret<br/>
    - Auto-scaler logic did not account for repeated failures<br/>
    <h3>Resolution</h3>
    Added automation to cap scale-up for failed pods and improve alerting.<br/>
    <h3>Takeaway</h3>
    Safeguard auto-scaling so it doesn't drive infinite resource creation on crash loops!
  `,
  },
  {
    id: "k8s-ingress-load-balancer-broken",
    date: "2025-07-26",
    title: "Broken Load Balancer: K8s Ingress Nightmare",
    subHeading: "DNS and Traffic Puzzle",
    labels: ["Kubernetes", "Incident", "DevOps"],
    summary:
      "Incorrect TLS config killed Ingress for hours during a product launch. Go behind the scenes as the SRE team worked under pressure.",
    fullContent: `
    <h2>Incident</h2>
    The launch coincided with a failed Ingress update which broke TLS and DNS routing.<br/>
    <h3>Timeline</h3>
    - Wrong secret referenced in Ingress YAML<br/>
    - DNS propagation delayed by 45 minutes<br/>
    - Manual rollback restored service<br/>
    <h3>Lessons</h3>
    Always run blue/green or staged Ingress updates and pre-validate DNS setup!
  `,
  },
  {
    id: "aws-iam-policy-locked-out",
    date: "2025-07-19",
    title: "AWS IAM Policy Oversight Locked Out Devs",
    subHeading: "Access Control in Real-world SaaS",
    labels: ["AWS", "Security", "Incident"],
    summary:
      "A change to IAM roles locked out developers from staging environments for 5 hours. This post explains how policy reviews and separation of power are critical.",
    fullContent: `
    <h2>Incident</h2>
    A well-intentioned change to tighten IAM policies inadvertently removed all dev users from staging access.<br/>
    <h3>Resolution</h3>
    - Admin role had to be recovered via root credentials<br/>
    - Reinstated developer group access<br/>
    <h3>Key Takeaway</h3>
    Always verify role permissions with a dry run and never make role changes without a backup admin!
  `,
  },
  {
    id: "argocd-gitops-wrong-tag-prod",
    date: "2025-07-12",
    title: "GitOps Disaster: ArgoCD Wrong Tag Deployed",
    subHeading: "Unexpected Production Outage",
    labels: ["CI/CD", "Kubernetes", "GitOps"],
    summary:
      "A typo in ArgoCD workflow caused an old image tag to be deployed on production. Learn rollback strategies and the importance of tag validation.",
    fullContent: `
    <h2>Event</h2>
    A minor typo led to the selection of the previous stable tag rather than the intended release.<br/>
    <h3>Impact</h3>
    - Outdated app version on prod<br/>
    - No breaking bug, but user features vanished<br/>
    <h3>Resolution</h3>
    Instantly reverted and added tag validation checks to all GitOps workflows.<br/>
    <h3>Lesson</h3>
    Validate tags and prefer strict selectors in ArgoCD!
  `,
  },
  {
    id: "docker-volume-race-condition",
    date: "2025-07-09",
    title: "Docker Volume Race Condition: Data Lost Midday",
    subHeading: "Concurrency Bug in Persistent Storage",
    labels: ["Docker", "Incident", "Storage"],
    summary:
      "A Docker volume mount race condition resulted in file loss for dozens of users. This article details storage best practices for Kubernetes and Docker.",
    fullContent: `
    <h2>Root Cause</h2>
    A race condition occurred when multiple containers attempted to attach to the same Docker volume simultaneously.<br/>
    <h3>Losses</h3>
    - Files were overwritten during pod startup<br/>
    - Affected close to 60 active users<br/>
    <h3>Prevention</h3>
    - Enforced single-writer policies<br/>
    - Added health checks for stateful pods<br/>
    <h3>Final Word</h3>
    Always treat shared persistent volumes with extreme care.
  `,
  },
  {
    id: "k8s-fluentd-logging-bills",
    date: "2025-07-05",
    title: "Expensive K8s Logging: Fluentd Misconfiguration",
    subHeading: "Infra Bills on the Rise",
    labels: ["Kubernetes", "Logging", "Incident"],
    summary:
      "Incorrect log routing with Fluentd led to redundancy and sky-high S3 storage costs. Discover how the team fixed their log data pipelines.",
    fullContent: `
    <h2>Incident Details</h2>
    A misconfigured Fluentd pipeline routed the same log stream to S3 twice.<br/>
    <h3>Impact</h3>
    - Cloud storage costs doubled overnight<br/>
    <h3>Fix</h3>
    - Audited Kubernetes log agent config<br/>
    - Removed duplicate output stanzas<br/>
    <h3>Advice</h3>
    Observability is great but always watch for config errors that impact costs!
  `,
  },
  {
    id: "aws-multi-cluster-monitoring-mayhem",
    date: "2025-07-01",
    title: "Multi-Cluster Monitoring Mayhem in AWS",
    subHeading: "Prometheus Federation Fiasco",
    labels: ["AWS", "Kubernetes", "Monitoring"],
    summary:
      "Federating Prometheus across AWS clusters caused missing metrics and confusion a lesson in distributed system monitoring gone wrong.",
    fullContent: `
    <h2>The Project</h2>
    We attempted to federate multiple Prometheus instances across three AWS EKS clusters.<br/>
    <h3>Issues Faced</h3>
    - Inconsistent label schemas<br/>
    - Missing cross-cluster metrics<br/>
    - Federation rules not matching targets<br/>
    <h3>Lessons Learned</h3>
    Always unify metrics schemas and validate Prometheus rules before merging clusters!
  `,
  },
];
